import { Component, HostListener, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet, Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { SidebarComponent } from './components';
import { SessionLockWarningComponent } from './components/session-lock-warning/session-lock-warning.component';
import { TrainingModeBannerComponent } from './components/training-mode-banner/training-mode-banner.component';
import { PwaBannersComponent } from './components/pwa-banners/pwa-banners.component';
import { JwtService } from './services/jwt.service';
import { SessionLockService } from './services/session-lock.service';
import { FiscalService } from './services/fiscal.service';
import { FullscreenService } from './services/fullscreen.service';
import { PwaUpdateService } from './services/pwa-update.service';
import { ConnectivityService } from './services/connectivity.service';
import { WakeLockService } from './services/wake-lock.service';
import { PwaInstallService } from './services/pwa-install.service';
import { AppBadgeService } from './services/app-badge.service';
import { filter } from 'rxjs/operators';

/** Below this width we switch the sidebar to "tablet mode":
 *   - the rail (80 px) is always reserved in the flex layout,
 *   - the expanded panel renders as an overlay on top of the page (with a
 *     backdrop) instead of pushing content narrower.
 *
 *  Covers the tablet target (iPad 9.7" portrait 768, iPad Pro 11" landscape
 *  1194) and narrow desktop windows. Above this, the sidebar behaves as a
 *  regular in-flow `nz-sider` that the user can manually expand/collapse.
 */
const OVERLAY_BREAKPOINT_PX = 1200;

@Component({
  selector: 'app-root',
  imports: [
    CommonModule,
    RouterOutlet,
    NzIconModule,
    NzLayoutModule,
    SidebarComponent,
    SessionLockWarningComponent,
    TrainingModeBannerComponent,
    PwaBannersComponent,
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  jwtService = inject(JwtService);
  router = inject(Router);
  private readonly sessionLock = inject(SessionLockService);
  private readonly fiscalService = inject(FiscalService);
  private fullscreenService = inject(FullscreenService);
  private readonly route = inject(ActivatedRoute);
  private readonly pwaUpdate = inject(PwaUpdateService);
  private readonly wakeLock = inject(WakeLockService);
  // Injected for their side effects: ConnectivityService wires its window
  // listeners on construction, AppBadgeService its unread-count effect.
  private readonly connectivity = inject(ConnectivityService);
  private readonly pwaInstall = inject(PwaInstallService);
  private readonly appBadge = inject(AppBadgeService);

  readonly isFullscreen = this.fullscreenService.active;

  /** User-facing collapse state. On desktop = nz-sider's nzCollapsed.
   *  On tablet, drives the overlay (expanded) vs rail-only (collapsed). */
  isCollapsed = false;
  /** True when viewport < OVERLAY_BREAKPOINT_PX — sidebar runs in tablet
   *  (overlay) mode. */
  isOverlayMode = false;
  isLoginPage = false;
  isPublicPage = false;

  ngOnInit() {
    this.isLoginPage = this.router.url === '/login' || this.router.url === '/pin-entry';
    this.applyViewportMode();
    this.publishViewportHeight();
    this.sessionLock.start();
    void this.fiscalService.loadMode();
    this.pwaUpdate.init();
    this.applyWakeLock();

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.isLoginPage = event.url === '/login' || event.url === '/pin-entry';
        // A navigation is activity, and it is also when a fresh login lands —
        // the mode is unknown until someone is logged in with a restaurant.
        this.sessionLock.markActivity();
        if (this.fiscalService.mode() === null && this.jwtService.isLogged()) {
          void this.fiscalService.loadMode();
        }
        // Fullscreen hides the sidebar, so leaving the page that owns it would
        // strand the user with no way to navigate.
        this.fullscreenService.exit();
        // Tablet drawer UX: navigating closes the overlay so the user sees
        // the page they just opened.
        if (this.isOverlayMode && !this.isCollapsed) {
          this.isCollapsed = true;
        }
        this.applyWakeLock();
        this.isPublicPage = !!this.deepestRoute().snapshot.data['publicPage'];
        this.pwaUpdate.checkThrottled();
      });
  }

  /** Only the service-floor screens opt in (`data.wakeLock`): holding the
   *  screen awake on the admin pages would flatten a tablet for nothing. */
  private applyWakeLock(): void {
    if (this.deepestRoute().snapshot.data['wakeLock']) {
      void this.wakeLock.enable();
    } else {
      void this.wakeLock.disable();
    }
  }

  private deepestRoute(): ActivatedRoute {
    let route = this.route;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  @HostListener('window:resize')
  onResize() {
    this.applyViewportMode();
    this.publishViewportHeight();
  }

  /** Mirror `window.innerHeight` into the `--app-vh` CSS variable on the
   *  root so the sidebar can pin to the *actual* viewport height. `100dvh`
   *  is theoretically equivalent but unreliable on some browsers / PWAs
   *  (mobile Safari, embedded webviews) — using the live JS value removes
   *  the guesswork. */
  private publishViewportHeight() {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    document.documentElement.style.setProperty('--app-vh', `${window.innerHeight}px`);
  }

  /** Recomputes overlay mode and applies the auto-collapse / auto-expand:
   *  - narrow viewport (< 1200) → force collapsed (rail-only, expandable as overlay)
   *  - wide viewport (≥ 1200)   → force expanded; collapsing is disabled on
   *                                desktop per product decision, so any
   *                                stale collapsed state from a previous
   *                                resize-down is undone here. */
  private applyViewportMode() {
    if (typeof window === 'undefined') {
      return;
    }
    this.isOverlayMode = window.innerWidth < OVERLAY_BREAKPOINT_PX;
    if (this.isOverlayMode) {
      this.isCollapsed = true;
    } else {
      this.isCollapsed = false;
    }
  }

  onCollapsedChange(collapsed: boolean) {
    this.isCollapsed = collapsed;
  }

  /** What `nz-sider` reserves in the flex layout. On tablet we always hold
   *  the rail (80 px) — the expanded panel never pushes the page narrower. */
  get siderCollapsed(): boolean {
    return this.isOverlayMode ? true : this.isCollapsed;
  }

  /** When true, the inner `app-sidebar` renders as an absolute-positioned
   *  panel on top of the page. */
  get isOverlayExpanded(): boolean {
    return this.isOverlayMode && !this.isCollapsed;
  }

  get showLayout(): boolean {
    return this.jwtService.isLogged() && !this.isLoginPage && !this.isPublicPage;
  }
}
